import {m_taxonomy} from "~/services/classes/TaxonomyClasses";

/**
 * Edits on the API tree, matching what the current builder sends to `store_state`. Each returns a
 * new tree; the input is never changed.
 */

function mapTree(node: m_taxonomy.Tree, fn: (n: m_taxonomy.Tree) => m_taxonomy.Tree): m_taxonomy.Tree {
    const mapped = fn(node);
    return {...mapped, children: mapped.children.map(c => mapTree(c, fn))};
}

function withAuthor(values: m_taxonomy.Data, authorId: number): m_taxonomy.Data {
    const next = {...values, authors: [...(values.authors ?? [])]};
    m_taxonomy.addAuthor(next, authorId);
    return next;
}

export type NewCategory = {
    /** `taxonomy.next_node_id` */
    id: number;
    label: string;
    description: string;
};

/** Adds a category with no figures yet as the last child of `parentId`. */
export function addCategory(tree: m_taxonomy.Tree, parentId: number, category: NewCategory, authorId: number): m_taxonomy.Tree {
    const node: m_taxonomy.Tree = {
        id: category.id,
        label: category.label,
        values: {
            description: category.description,
            p__id__count: 0,
            p__spend__sum: 0,
            s__id__nunique: 0,
            authors: [authorId],
        },
        sources: [],
        removed: false,
        children: [],
    };
    return mapTree(tree, n => n.id === parentId ? {...n, children: [...n.children, node]} : n);
}

/** Marks a category and its whole branch as removed (a soft delete, as in the current builder). */
export function removeCategory(tree: m_taxonomy.Tree, id: number, authorId: number): m_taxonomy.Tree {
    const markBranch = (n: m_taxonomy.Tree): m_taxonomy.Tree => ({
        ...n,
        removed: true,
        values: withAuthor(n.values, authorId),
        children: n.children.map(markBranch),
    });
    return mapTree(tree, n => n.id === id ? markBranch(n) : n);
}

/**
 * Gives a category a new name and description; the backend reports it as `renamed` against the
 * original (and `updated` when the description changed too).
 */
export function renameCategory(
    tree: m_taxonomy.Tree,
    id: number,
    label: string,
    description: string,
    authorId: number,
): m_taxonomy.Tree {
    return mapTree(tree, n => n.id === id
        ? {...n, label, values: {...withAuthor(n.values, authorId), description}}
        : n);
}

function findNode(node: m_taxonomy.Tree, id: number): m_taxonomy.Tree | undefined {
    if (node.id === id) return node;
    for (const child of node.children) {
        const found = findNode(child, id);
        if (found) return found;
    }
    return undefined;
}

/**
 * Moves a category, with its whole branch, to the end of `toId`'s children (the current builder's
 * move; only the moved category gets the author). The backend reports it as `moved`.
 */
export function moveCategory(tree: m_taxonomy.Tree, id: number, toId: number, authorId: number): m_taxonomy.Tree {
    const source = findNode(tree, id);
    if (!source) return tree;
    const moved = {...source, values: withAuthor(source.values, authorId)};
    return mapTree(tree, n => {
        const children = n.children.filter(c => c.id !== id);
        return {...n, children: n.id === toId ? [...children, moved] : children};
    });
}

export type MergedCategory = {
    /** `taxonomy.next_node_id` */
    id: number;
    label: string;
    description: string;
};

/**
 * Merges sibling categories into one new category, as the current builder does: it takes the place
 * of the first of them, sums their figures, keeps all their sources (so the backend reports it as
 * merged) and authors, takes the first of their approval notes, and gets all their sub-categories. The merged categories are removed from
 * the tree outright, not marked as removed. Unlike the current builder, the description you enter is
 * saved (the current builder drops it: `createMergedNode` always saves an empty description, TODO: check this with Dennis.).
 *
 */
export function mergeCategories(
    tree: m_taxonomy.Tree,
    parentId: number,
    ids: readonly number[],
    merged: MergedCategory,
    authorId: number,
): m_taxonomy.Tree {
    return mapTree(tree, n => {
        if (n.id !== parentId) return n;
        // In the order given (the order they were selected), like the current builder.
        const sources = ids.flatMap(id => n.children.filter(c => c.id === id));
        if (sources.length < 2) return n;
        const sum = (field: 'p__id__count' | 'p__spend__sum' | 's__id__nunique') =>
            sources.reduce((total, c) => total + (c.values[field] ?? 0), 0);
        // Like the current builder's `mergeValues`: the first value among the merged categories is kept.
        const first = <K extends 'approval_note' | 'approval_feedback_note' | 'approval_touched'>(field: K) =>
            sources.find(c => c.values[field] !== undefined)?.values[field];
        const approvalNote = first('approval_note');
        const approvalFeedbackNote = first('approval_feedback_note');
        const approvalTouched = first('approval_touched');
        const node: m_taxonomy.Tree = {
            id: merged.id,
            label: merged.label,
            values: {
                ...(approvalNote !== undefined && {approval_note: approvalNote}),
                ...(approvalFeedbackNote !== undefined && {approval_feedback_note: approvalFeedbackNote}),
                ...(approvalTouched !== undefined && {approval_touched: approvalTouched}),
                description: merged.description,
                p__id__count: sum('p__id__count'),
                p__spend__sum: sum('p__spend__sum'),
                // Summed like the current builder: a supplier in several of them is counted more than once.
                s__id__nunique: sum('s__id__nunique'),
                authors: [...new Set([...sources.flatMap(c => c.values.authors ?? []), authorId])],
            },
            sources: sources.flatMap(c => c.sources),
            removed: false,
            children: sources.flatMap(c => c.children),
        };
        // Where the first selected one was: after the remaining categories that came before it.
        const firstAt = n.children.indexOf(sources[0]);
        const at = n.children.slice(0, firstAt).filter(c => !ids.includes(c.id)).length;
        const rest = n.children.filter(c => !ids.includes(c.id));
        return {...n, children: [...rest.slice(0, at), node, ...rest.slice(at)]};
    });
}

/** The tree as the backend expects it: per node, `exportNode` drops the computed `changes`. */
export function exportTaxonomyTree(node: m_taxonomy.Tree): m_taxonomy.Tree {
    return {...m_taxonomy.exportNode(node), children: node.children.map(exportTaxonomyTree)};
}
